/**
 * Liegt das Dokument vor, das der nächste Schritt braucht?
 *
 * Zwei Fragen, die vor dem Versand zu klären sind — beide führten schon zu Mails, die beim
 * Kunden ins Leere liefen: eine Unterschriftsanfrage ohne Bericht (er konnte nur den
 * Kontoauszug hochladen) und eine VN-Anfrage ohne VN-Formular.
 */

const grundtyp = (kategorie: string | null | undefined): string => (kategorie ?? '').trim().toLowerCase();

/** Bericht-PDF am Vorgang — auch die unterschriebenen Fassungen zählen. */
export function hatBerichtPdf(kategorien: Array<string | null | undefined>): boolean {
  return kategorien.some((k) => grundtyp(k).startsWith('bericht pdf'));
}

/** Verwendungsnachweis am Vorgang — roh oder unterschrieben. */
export function hatVerwendungsnachweis(kategorien: Array<string | null | undefined>): boolean {
  return kategorien.some((k) => grundtyp(k).includes('verwendungsnachweis'));
}

/** Der Bericht ist unterschrieben zurück (Kunde oder vollständig). */
export function hatBerichtUnterschrieben(kategorien: Array<string | null | undefined>): boolean {
  return kategorien.some((k) => {
    const c = grundtyp(k);
    return c.startsWith('bericht pdf') && c.includes('unterschrieben');
  });
}

/**
 * Darf der Bericht noch komplett neu erzeugt werden?
 *
 * Sobald er beim Kunden liegt — verschickt oder unterschrieben zurück — heißt „neu
 * erstellen" in Wahrheit: alle Texte überschreiben, während draußen eine andere Fassung
 * unterschrieben wird. Der Knopf verschwindet deshalb; wer wirklich neu schreiben will,
 * kann die Abschnitte einzeln erzeugen.
 */
export function darfNeuErstellen(input: {
  kategorien: Array<string | null | undefined>;
  signaturRunde: 'none' | 'pending' | 'completed';
}): boolean {
  if (input.signaturRunde !== 'none') return false;
  return !hatBerichtUnterschrieben(input.kategorien);
}

export type EditorSchritt = 'speichern' | 'unterschrift_anfordern' | 'vn_anfordern';

/**
 * Der nächste Schritt im Editor — als EIN Knopf über „Bericht komplett erstellen".
 *
 * Von hinten nach vorn, wie überall im Haus: Der am weitesten fortgeschrittene zutreffende
 * Zustand gewinnt. Ohne Bericht-PDF heißt der Schritt „speichern" — auch wenn noch
 * Abschnitte fehlen. Das ist Absicht: Ein Zwischenstand darf gespeichert werden, und ohne
 * Datei am Vorgang geht ohnehin nichts weiter (die Unterschriftsmail liefe ins Leere).
 */
export function naechsterEditorSchritt(input: {
  kategorien: Array<string | null | undefined>;
}): EditorSchritt {
  if (hatBerichtUnterschrieben(input.kategorien)) return 'vn_anfordern';
  if (hatBerichtPdf(input.kategorien)) return 'unterschrift_anfordern';
  return 'speichern';
}
